import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiBell, FiExternalLink } from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import TableSkeleton from "./listing/TableSkeleton";
import ListingEmptyState from "./listing/ListingEmptyState";
import ListingPaginationFooter from "./listing/ListingPaginationFooter";
import ListingFilterHeader from "./listing/ListingFilterHeader";
import StatusBadge from "./listing/StatusBadge";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_ICON_ACTION_CLASS,
  LISTING_PAGE_CLASS,
} from "./listing/listingStyles";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../service/CallCenterNotifications";

const PAGE_SIZE = 20;

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const NotificationListPage = ({ notificationType, titleKey, emptyMessageKey, fallbackTitleKey }) => {
  const t = useCallCenterT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const totalPages = Math.max(Math.ceil(totalElements / PAGE_SIZE), totalElements > 0 ? 1 : 0);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listNotifications({
        page,
        size: PAGE_SIZE,
        type: notificationType,
        unreadOnly,
      });
      setItems(Array.isArray(response?.items) ? response.items : []);
      setTotalElements(Number(response?.total) || 0);
      setUnreadCount(Number(response?.unreadCount) || 0);
    } catch (error) {
      toast.error(t("ccNotificationsLoadFailed"));
      setItems([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [notificationType, page, unreadOnly, t]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleOpenNotification = async (notification) => {
    try {
      if (!notification.read) {
        await markNotificationRead(notification.id);
        setItems((prev) =>
          prev.map((item) => (item.id === notification.id ? { ...item, read: true } : item))
        );
        setUnreadCount((count) => Math.max(0, count - 1));
      }
      if (notification.ticketId) {
        const params = new URLSearchParams({ ticketId: notification.ticketId });
        if (notification.accountId) {
          params.set("accountNo", notification.accountId);
        }
        navigate(`/call-center/tickets?${params.toString()}`);
      }
    } catch (error) {
      toast.error(t("ccNotificationsMarkReadFailed"));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      await markAllNotificationsRead({ type: notificationType });
      setUnreadCount(0);
      setItems((prev) => prev.map((item) => ({ ...item, read: true })));
      toast.success(t("ccNotificationsMarkAllReadSuccess"));
    } catch (error) {
      toast.error(t("ccNotificationsMarkAllReadFailed"));
    } finally {
      setMarkingAll(false);
    }
  };

  const subtitle =
    unreadCount === 1
      ? t("ccNotificationsSubtitleOne")
      : t("ccNotificationsSubtitleMany").replace("{count}", String(unreadCount));

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">{t(titleKey)}</h1>
            <p className="mt-1 text-sm text-[#6b7280]">{subtitle}</p>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={markingAll}
              className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              {markingAll ? t("ccProcessing") : t("ccNotificationsMarkAllRead")}
            </button>
          )}
        </header>

        <section className={LISTING_FILTER_CARD_CLASS}>
          <ListingFilterHeader
            title={t("ccListFiltersTitle")}
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            resultsLabel={t("ccNotificationsResultsLabel")}
            showingPageLabel={t("ccShowingPage")}
            ofLabel={t("ccOf")}
            refreshLabel={t("ccRefresh")}
            loading={loading}
            onRefresh={loadNotifications}
          />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-4">
              <label className={FILTER_LABEL_CLASS}>{t("ccStatus")}</label>
              <select
                value={unreadOnly ? "unread" : "all"}
                onChange={(event) => {
                  setPage(0);
                  setUnreadOnly(event.target.value === "unread");
                }}
                className={FILTER_FIELD_CLASS}
              >
                <option value="all">{t("ccAll")}</option>
                <option value="unread">{t("ccNotificationsUnreadOnly")}</option>
              </select>
            </div>
          </div>
        </section>

        <section className={LISTING_CARD_CLASS}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50/80 text-left">
                <tr>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t("ccStatus")}
                  </th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t("ccNotificationsColumnTitle")}
                  </th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t("ccNotificationsColumnMessage")}
                  </th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t("ccNotificationsColumnDate")}
                  </th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t("ccActions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton rows={5} columns={5} />
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <ListingEmptyState
                        icon={FiBell}
                        title={t("ccNotificationsEmptyTitle")}
                        hint={t(emptyMessageKey)}
                      />
                    </td>
                  </tr>
                ) : (
                  items.map((notification) => (
                    <tr
                      key={notification.id}
                      className={`border-t border-slate-100 transition hover:bg-slate-50/70 ${
                        notification.read ? "" : "bg-emerald-50/30"
                      }`}
                    >
                      <td className="px-4 py-4">
                        <StatusBadge
                          status={notification.read ? "READ" : "UNREAD"}
                          label={notification.read ? t("ccRead") : t("ccUnread")}
                        />
                      </td>
                      <td className="px-4 py-4 font-semibold text-slate-900">
                        {notification.title || t(fallbackTitleKey)}
                      </td>
                      <td className="max-w-md px-4 py-4 text-slate-600">
                        <div className="truncate" title={notification.body}>
                          {notification.body || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-slate-500">
                        {formatTimestamp(notification.createdAt)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => handleOpenNotification(notification)}
                            title={notification.ticketId ? t("ccAccountPlanOpenTicket") : t("ccOpen")}
                            className={LISTING_ICON_ACTION_CLASS}
                          >
                            <FiExternalLink className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <ListingPaginationFooter
            totalElements={totalElements}
            resultsLabel={t("ccNotificationsResultsLabel")}
            page={page}
            totalPages={totalPages}
            loading={loading}
            onPrevious={() => setPage((value) => Math.max(0, value - 1))}
            onNext={() => setPage((value) => value + 1)}
            previousLabel={t("ccPrevious")}
            nextLabel={t("ccNext")}
          />
        </section>
      </div>
    </div>
  );
};

export default NotificationListPage;
